import { TableFilter, TableSortSelect } from '../components/TableControls'
import { matchesTableSearch, sortTableRows, useTableSort } from '../lib/tableControls'
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Search,
  Settings,
  MoreHorizontal,
  Plus,
  Coins,
  Bot,
  Archive,
  Trash2,
} from "lucide-react";

import { supabase, signOut, getCurrentSession } from "../lib/supabase";
import {
  daysAgoKey,
  emptyTotals,
  fetchTeamAgentUsageDaily,
  sumUsage,
  toDateKey,
  type AgentUsageDailyRow,
  type UsageTotals,
} from "../lib/agentUsage";
import {
  LIST_PRICE_LABEL,
  TokenBreakdown,
  UsageCostText,
} from "../components/UsageCost";
import { ProjectAttribution } from "../components/Projects/ProjectAttribution";
import { AppSidebar } from "../components/AppSidebar";
import { TopBar } from "../components/TopBar";
import { SidebarInset, SidebarProvider } from "../components/ui/sidebar";
import { Skeleton } from "../components/ui/skeleton";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { Checkbox } from "../components/ui/checkbox";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from "../components/ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { buttonVariants } from "../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

// ============================================================================
// Page: /dashboard/team/:teamId/projects
//
// Admin-created projects (see 20260909100000_add_projects.sql) -- explicitly
// NOT derived from git_repo/folder names (that's still TopProjects.tsx on
// the main dashboard, unchanged and running in parallel). A project here can
// be assigned to multiple pods (reporting/visibility scope only), and shows
// the token spend of every agent that's been pinned to it
// (agents.project_id -- a fixed, self-service, one-agent-one-project
// assignment made from the agent's own detail page, not from here).
//
// Layout/data patterns lifted directly from TeamManagement.tsx (list +
// detail split, manage-* edge function for writes, window.prompt for a
// quick rename) so this reads as the same app rather than a bolted-on
// feature.
//
// Token spend window: trailing 7 days, keyed off usage_date. 2026-10-02
// (Step 6 of docs/TOKEN_COST_ACCURACY_PLAN.md): tokens/$ come from
// get_team_agent_usage_daily via src/lib/agentUsage.ts (server-priced list-
// price estimate, cache tokens included), not agent_checkin.tokens_* + a
// client-side rate table. Unpriced usage is shown as "N unpriced", never $0.
// A project's spend is attributed by the RPC's own project_id column, not by
// joining agent -> project here: the RPC returns agent_id NULL for agents the
// caller can't see in detail, but those rows still carry project_id, so the
// project total stays a true team-wide figure for every role.
//
// 2026-10-02 (Phase 1.1): project_id on those rows is now resolved PER EVENT
// at read time (.syncsimple.json hint -> project_rules -> agent default ->
// Unassigned), so "spend" here is no longer just the agents pinned to the
// project. Rules + the Unassigned panel live in
// components/Projects/ProjectAttribution.tsx; after a rule change it calls
// refreshUsage() so these figures pick up the new attribution.
// ============================================================================

interface ProjectsProps {
  teamId: string;
}

interface Project {
  id: string;
  name: string;
  description: string | null;
  created_at: string;
  archived_at: string | null;
}

interface Pod {
  id: string;
  name: string;
}

// Minimal subset of TeamManagement.tsx's CombinedUser -- just enough to
// list a person in the Members checklist below and resolve a display name.
// Kept separate rather than importing CombinedUser so this page doesn't
// depend on TeamManagement's full user-management shape.
interface MemberOption {
  auth_user_id: string | null;
  user_name: string | null;
  user_email: string | null;
}

interface AgentRow {
  id: string;
  name: string;
  project_id: string | null;
}

function memberDisplayName(
  m: Pick<MemberOption, "user_name" | "user_email">,
): string {
  return m.user_name || m.user_email?.split("@")[0] || "Unknown user";
}

function initialsOf(name: string): string {
  return (
    name
      .split(/[\s-]+/)
      .map((w) => w[0])
      .filter(Boolean)
      .slice(0, 2)
      .join("")
      .toUpperCase() || "?"
  );
}

const PROJECT_AVATAR_PALETTE = [
  "bg-pink-500",
  "bg-blue-500",
  "bg-emerald-600",
  "bg-violet-600",
  "bg-amber-500",
  "bg-cyan-600",
];

function paletteFor(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i++)
    hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  return PROJECT_AVATAR_PALETTE[hash % PROJECT_AVATAR_PALETTE.length];
}

// ROLE AUDIT (2026-09-29, three-tier permission model -- see
// docs/ALERTING_PERMISSIONS_PLAN.md): explicit decision to leave project
// creation/pod-assignment admin-only, not admin+manager -- projects aren't
// pod-scoped in the schema (no pod_id on projects), so "a manager sees only
// their pod's projects" isn't a well-defined restriction without further
// product design. Not changed here.
//
// SIDE EFFECT worth knowing about: this page's own agent_checkin query below
// (for per-project cost totals) is NOT gated on isAdmin -- it always ran for
// whoever loaded this page. Now that agent_checkin's SELECT policy is split
// into admin/manager-only (20260928201832_add_pod_managers_table.sql), a
// plain member reaching this page gets zero checkin rows back (RLS, not an
// error) instead of the full-team totals it silently showed before -- cost
// figures will now read as empty/$0 for members rather than erroring.
// UPDATE 2026-10-05: AppSidebar.tsx now checks role (admin: Team Management
// + Billing; admin+manager: Projects + Config) -- see its own comment. That
// only hides the nav entry, though; this page's own isAdmin/isManager state
// below remains the actual content-level gate, since nothing redirects a
// plain member who reaches this URL directly.
//
// Also 2026-10-05: Members on this project (below, next to "Pods on this
// project") are now a direct, admin-curated roster (`project_members`),
// not derived from pod membership -- see 20261005000000_add_project_members.sql
// for why the old pod-union approach over-counted. `project_pods` keeps its
// existing job as a department/pod tag, independent of who's actually on
// the project.
const Projects: React.FC<ProjectsProps> = ({ teamId }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [teamName, setTeamName] = useState<string | undefined>(undefined);
  const [isAdmin, setIsAdmin] = useState(false);
  // Attribution rules + unassigned usage (Phase 1.1): admins edit, managers
  // view, members don't see the section at all (matches RLS on project_rules
  // and get_team_unassigned_usage's own scoping).
  const [isManager, setIsManager] = useState(false);

  const [projects, setProjects] = useState<Project[]>([]);
  const [pods, setPods] = useState<Pod[]>([]);
  const [projectPodIds, setProjectPodIds] = useState<Map<string, Set<string>>>(
    new Map(),
  );
  const [teamMembers, setTeamMembers] = useState<MemberOption[]>([]);
  const [projectMemberIds, setProjectMemberIds] = useState<
    Map<string, Set<string>>
  >(new Map());
  const [agents, setAgents] = useState<AgentRow[]>([]);
  const [usageRows, setUsageRows] = useState<AgentUsageDailyRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Deep-link target for ProjectRulesNudge.tsx's "Set up a rule" link
  // (/dashboard/team/:teamId/projects#attribution-rules). A plain anchor
  // scroll only works on a real page load -- this is a client-side route
  // change, so the browser never does it on its own. Keyed on `loading`
  // rather than mount, since the Attribution Rules card (and this hash
  // target) doesn't exist in the DOM until data has loaded.
  useEffect(() => {
    if (loading || !location.hash) return;
    const id = location.hash.slice(1);
    const el = document.getElementById(id);
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [loading, location.hash]);

  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(
    null,
  );
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState('all');
  const [teamFilter, setTeamFilter] = useState('all');
  const { sort, setSort } = useTableSort('name');

  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetMode, setSheetMode] = useState<"create" | "edit">("create");
  const [sheetTarget, setSheetTarget] = useState<Project | null>(null);
  const [sheetName, setSheetName] = useState("");
  const [sheetDescription, setSheetDescription] = useState("");
  const [saving, setSaving] = useState(false);

  const [togglingPodId, setTogglingPodId] = useState<string | null>(null);
  const [togglingMemberId, setTogglingMemberId] = useState<string | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<Project | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchUsageWindow = () =>
    fetchTeamAgentUsageDaily(teamId, daysAgoKey(6), toDateKey(new Date()));

  const loadAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const session = await getCurrentSession();

      const [teamRes, projectsRes, podsRes, membersRes, agentsRes] =
        await Promise.all([
          supabase.from("teams").select("name").eq("id", teamId).single(),
          (supabase.from as any)("projects")
            .select("id, name, description, created_at, archived_at")
            .eq("team_id", teamId)
            .is("deleted_at", null)
            .order("created_at", { ascending: false }),
          (supabase.from as any)("pods").select("id, name").eq("team_id", teamId),
          // Same source TeamManagement.tsx uses for its own member list --
          // RLS on `users`/`team_members` doesn't expose a clean team-wide
          // SELECT, so membership resolution goes through this edge function
          // rather than a direct client query (see get-all-team-users).
          session
            ? supabase.functions.invoke("get-all-team-users", {
                body: { team_id: teamId },
                headers: { Authorization: `Bearer ${session.access_token}` },
              })
            : Promise.resolve({ data: { users: [] }, error: null }),
          (supabase.from as any)("agents_team_visible")
            .select("id, name, project_id")
            .eq("team_id", teamId),
        ]);

      if (teamRes.error) throw teamRes.error;
      if (projectsRes.error) throw projectsRes.error;
      if (podsRes.error) throw podsRes.error;
      if (agentsRes.error) throw agentsRes.error;

      setTeamName(teamRes.data?.name ?? undefined);
      const projectRows = (projectsRes.data ?? []) as Project[];
      setProjects(projectRows);
      setPods((podsRes.data ?? []) as Pod[]);
      setTeamMembers((membersRes.data?.users ?? []) as MemberOption[]);

      // project_pods/project_members have no team_id column of their own
      // (see 20260909100000_add_projects.sql) -- RLS already stops
      // cross-tenant reads, but without this id list the two queries used to
      // pull every project-pod/member row the signed-in user can see across
      // ALL their teams, not just this one. Depends on projectRows above, so
      // it can't join the Promise.all those come from.
      const projectIds = projectRows.map((p) => p.id);
      const [projectPodsRes, projectMembersRes] =
        projectIds.length > 0
          ? await Promise.all([
              (supabase.from as any)("project_pods")
                .select("project_id, pod_id")
                .in("project_id", projectIds),
              (supabase.from as any)("project_members")
                .select("project_id, auth_user_id")
                .in("project_id", projectIds),
            ])
          : [
              { data: [], error: null },
              { data: [], error: null },
            ];

      if (projectPodsRes.error) throw projectPodsRes.error;
      if (projectMembersRes.error) throw projectMembersRes.error;

      const podMap = new Map<string, Set<string>>();
      for (const row of (projectPodsRes.data ?? []) as {
        project_id: string;
        pod_id: string;
      }[]) {
        if (!podMap.has(row.project_id)) podMap.set(row.project_id, new Set());
        podMap.get(row.project_id)!.add(row.pod_id);
      }
      setProjectPodIds(podMap);

      const memberMap = new Map<string, Set<string>>();
      for (const row of (projectMembersRes.data ?? []) as {
        project_id: string;
        auth_user_id: string;
      }[]) {
        if (!memberMap.has(row.project_id))
          memberMap.set(row.project_id, new Set());
        memberMap.get(row.project_id)!.add(row.auth_user_id);
      }
      setProjectMemberIds(memberMap);

      const agentRows = (agentsRes.data ?? []) as AgentRow[];
      setAgents(agentRows);

      // Trailing 7 days, server-priced usage (see src/lib/agentUsage.ts).
      // Team-wide aggregate RPC, same exposure as TopProjects. No agent
      // filter: null-agent rows must be included in project totals.
      setUsageRows(await fetchUsageWindow());

      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (user) {
        const { data: tm } = await supabase
          .from("team_members")
          .select("role")
          .eq("team_id", teamId)
          .eq("auth_user_id", user.id)
          .single();
        setIsAdmin(tm?.role === "admin" || tm?.role === "owner");
        setIsManager(tm?.role === "manager");
      }

      setSelectedProjectId((prev) => {
        if (prev && projectRows.some((p) => p.id === prev)) return prev;
        return projectRows[0]?.id ?? null;
      });
    } catch (err) {
      console.error("Failed to load Projects data:", err);
      setError("Could not load projects for this team.");
    } finally {
      setLoading(false);
    }
  };

  // Re-run after an attribution rule changes: rules are applied at read time,
  // so project totals shift without any other data changing.
  const refreshUsage = useCallback(async () => {
    try {
      setUsageRows(await fetchUsageWindow());
    } catch (err) {
      console.error("Failed to refresh project usage:", err);
    }
  }, [teamId]);

  useEffect(() => {
    loadAll();
  }, [teamId]);

  const goToDashboard = () => navigate(`/dashboard/team/${teamId}`);
  const handleLogout = async () => {
    await signOut();
    navigate("/auth/login");
  };

  const filteredProjects = useMemo(() => sortTableRows(projects.filter(project => matchesTableSearch(search, project.name, project.description)
    && (statusFilter === 'all' || (statusFilter === 'archived' ? !!project.archived_at : !project.archived_at))
    && (teamFilter === 'all' || (teamFilter === 'unassigned' ? !projectPodIds.get(project.id)?.size : projectPodIds.get(project.id)?.has(teamFilter)))), sort, {
      name: project => project.name, created: project => Date.parse(project.created_at), status: project => project.archived_at ? 'Archived' : 'Active',
    }), [projects, search, statusFilter, teamFilter, projectPodIds, sort]);

  const selectedProject =
    projects.find((p) => p.id === selectedProjectId) ?? null;

  const agentCountByProject = useMemo(() => {
    const counts = new Map<string, number>();
    for (const a of agents) {
      if (!a.project_id) continue;
      counts.set(a.project_id, (counts.get(a.project_id) ?? 0) + 1);
    }
    return counts;
  }, [agents]);

  const projectAgents = useMemo(
    () =>
      selectedProject
        ? agents.filter((a) => a.project_id === selectedProject.id)
        : [],
    [agents, selectedProject],
  );

  const projectSpend = useMemo(() => {
    if (!selectedProject) return sumUsage([]);
    return sumUsage(
      usageRows.filter((r) => r.projectId === selectedProject.id),
    );
  }, [usageRows, selectedProject]);

  const assignedPodIds = selectedProject
    ? (projectPodIds.get(selectedProject.id) ?? new Set<string>())
    : new Set<string>();

  const assignedMemberIds = selectedProject
    ? (projectMemberIds.get(selectedProject.id) ?? new Set<string>())
    : new Set<string>();

  const openCreate = () => {
    setSheetMode("create");
    setSheetTarget(null);
    setSheetName("");
    setSheetDescription("");
    setSheetOpen(true);
  };

  const openEdit = (project: Project) => {
    setSheetMode("edit");
    setSheetTarget(project);
    setSheetName(project.name);
    setSheetDescription(project.description ?? "");
    setSheetOpen(true);
  };

  const handleSaveProject = async () => {
    if (!sheetName.trim()) return;
    setSaving(true);
    try {
      const session = await getCurrentSession();
      if (!session) throw new Error("No session found");

      const { data, error: fnError } = await supabase.functions.invoke(
        "manage-projects",
        {
          body:
            sheetMode === "create"
              ? {
                  action: "create",
                  team_id: teamId,
                  name: sheetName.trim(),
                  description: sheetDescription.trim() || null,
                }
              : {
                  action: "update",
                  team_id: teamId,
                  project_id: sheetTarget?.id,
                  name: sheetName.trim(),
                  description: sheetDescription.trim() || null,
                },
          headers: { Authorization: `Bearer ${session.access_token}` },
        },
      );
      if (fnError) throw fnError;
      if (data?.error) throw new Error(data.error);

      toast.success(
        sheetMode === "create" ? "Project created" : "Project updated",
      );
      setSheetOpen(false);
      await loadAll();
      if (sheetMode === "create" && data?.project?.id)
        setSelectedProjectId(data.project.id);
    } catch (err: any) {
      console.error("Failed to save project:", err);
      toast.error(err.message || "Failed to save project");
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const session = await getCurrentSession();
      if (!session) throw new Error("No session found");
      const { error: fnError } = await supabase.functions.invoke(
        "manage-projects",
        {
          body: {
            action: "delete",
            team_id: teamId,
            project_id: deleteTarget.id,
          },
          headers: { Authorization: `Bearer ${session.access_token}` },
        },
      );
      if (fnError) throw fnError;
      toast.success("Project deleted");
      setSelectedProjectId(null);
      setDeleteTarget(null);
      await loadAll();
    } catch (err: any) {
      toast.error(err.message || "Failed to delete project");
    } finally {
      setDeleting(false);
    }
  };

  // Mirrors ProjectsPanel.tsx's handleToggleArchive (Team Management ->
  // Projects tab) so a project archived/unarchived from either surface
  // behaves and reads the same way.
  const handleToggleArchive = async (project: Project) => {
    try {
      const session = await getCurrentSession();
      if (!session) throw new Error("No session found");
      const { data, error: fnError } = await supabase.functions.invoke(
        "manage-projects",
        {
          body: {
            action: project.archived_at ? "unarchive" : "archive",
            team_id: teamId,
            project_id: project.id,
          },
          headers: { Authorization: `Bearer ${session.access_token}` },
        },
      );
      if (fnError) throw fnError;
      if (data?.error) throw new Error(data.error);
      toast.success(
        project.archived_at ? "Project unarchived" : "Project archived",
      );
      await loadAll();
    } catch (err: any) {
      toast.error(err.message || "Failed to update project archive status");
    }
  };

  const handleTogglePod = async (pod: Pod, checked: boolean) => {
    if (!selectedProject) return;
    setTogglingPodId(pod.id);
    try {
      const session = await getCurrentSession();
      if (!session) throw new Error("No session found");
      const { error: fnError } = await supabase.functions.invoke(
        "manage-projects",
        {
          body: {
            action: checked ? "assign_pod" : "unassign_pod",
            team_id: teamId,
            project_id: selectedProject.id,
            pod_id: pod.id,
          },
          headers: { Authorization: `Bearer ${session.access_token}` },
        },
      );
      if (fnError) throw fnError;
      await loadAll();
    } catch (err: any) {
      toast.error(err.message || "Failed to update team assignment");
    } finally {
      setTogglingPodId(null);
    }
  };

  const handleToggleMember = async (member: MemberOption, checked: boolean) => {
    if (!selectedProject || !member.auth_user_id) return;
    setTogglingMemberId(member.auth_user_id);
    try {
      const session = await getCurrentSession();
      if (!session) throw new Error("No session found");
      const { error: fnError } = await supabase.functions.invoke(
        "manage-projects",
        {
          body: {
            action: checked ? "add_member" : "remove_member",
            team_id: teamId,
            project_id: selectedProject.id,
            member_auth_user_id: member.auth_user_id,
          },
          headers: { Authorization: `Bearer ${session.access_token}` },
        },
      );
      if (fnError) throw fnError;
      await loadAll();
    } catch (err: any) {
      toast.error(err.message || "Failed to update project membership");
    } finally {
      setTogglingMemberId(null);
    }
  };

  return (
    <SidebarProvider>
      <AppSidebar
        activeSection="projects"
        onNavigate={goToDashboard}
        teamId={teamId}
      />
      <SidebarInset>
        <TopBar
          teamId={teamId}
          teamName={teamName}
          onLogout={handleLogout}
          sectionLabel="Projects"
        />
        <main className="space-y-6 bg-background p-4 sm:p-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h1 className="mb-1 text-2xl font-semibold text-foreground">
                Projects
              </h1>
              <p className="text-sm text-muted-foreground">
                      Group connectors and teams under a project to see combined token
                spend. Projects are created by admins, not derived from repo or
                folder names.
              </p>
            </div>
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          {!error && (
            <div className="grid min-h-0 items-stretch grid-cols-1 gap-4 lg:grid-cols-[320px_1fr]">
              {/* Two separate panels with a real gap, not a seamless block --
                  matches TeamManagement.tsx's Teams/detail panel split
                  (Figma 2026-09-29; was gap-0 + border-r-0 here, the older
                  pattern that page since moved away from). */}
              <Card className="flex h-full flex-col overflow-hidden bg-background">
                <CardContent className="flex flex-1 flex-col space-y-3 p-4">
                  <div className="text-sm font-bold uppercase tracking-wide text-foreground">
                    Projects ({projects.length})
                  </div>
                  <div className="relative">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      aria-label="Search projects"
                      placeholder="Search projects..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="pl-8"
                    />
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <TableFilter label="Filter project status" value={statusFilter} onChange={setStatusFilter} allLabel="All statuses" options={[{ value: 'active', label: 'Active' }, { value: 'archived', label: 'Archived' }]} />
                    <TableFilter label="Filter project team" value={teamFilter} onChange={setTeamFilter} allLabel="All teams" options={[{ value: 'unassigned', label: 'Unassigned' }, ...pods.map(pod => ({ value: pod.id, label: pod.name }))]} />
                    <TableSortSelect sort={sort} onChange={setSort} options={[{ value: 'name', label: 'Name' }, { value: 'created', label: 'Created' }, { value: 'status', label: 'Status' }]} />
                    <span className="text-xs text-muted-foreground">{filteredProjects.length} of {projects.length} results</span>
                    {(!!search || statusFilter !== 'all' || teamFilter !== 'all') && <Button variant="ghost" size="sm" onClick={() => { setSearch(''); setStatusFilter('all'); setTeamFilter('all') }}>Clear filters</Button>}
                  </div>
                  <div className="flex-1 overflow-y-auto divide-y divide-border border-y border-border">
                    {loading ? (
                      // Shape mirrors a loaded project row (square avatar,
                      // name + agent-count line) so the shimmer reads as
                      // "projects incoming" rather than a generic bar --
                      // matches TeamManagement.tsx's Teams panel shimmer.
                      <>
                        {[0, 1, 2].map((i) => (
                          <div
                            key={i}
                            className="flex min-h-[44px] items-center gap-3 px-2 py-1.5"
                          >
                            <Skeleton className="h-9 w-9 shrink-0 rounded-md" />
                            <div className="min-w-0 flex-1 space-y-2">
                              <Skeleton className="h-4 w-24" />
                              <Skeleton className="h-3 w-16" />
                            </div>
                          </div>
                        ))}
                      </>
                    ) : (
                      <>
                        {filteredProjects.map((project) => (
                          <button
                            key={project.id}
                            type="button"
                            onClick={() => setSelectedProjectId(project.id)}
                            className={`flex w-full min-h-[44px] items-center gap-3 px-2 py-1.5 text-left transition-colors ${
                              selectedProjectId === project.id
                                ? "bg-muted"
                                : "hover:bg-muted/50"
                            } ${project.archived_at ? "opacity-60" : ""}`}
                          >
                            {/* Rounded-square avatar, same as TeamManagement.tsx's
                                Teams panel -- a project groups agents/pods the
                                same way a team groups members, so it gets the
                                team/pod avatar shape, not the circular person one. */}
                            <Avatar className="h-9 w-9 shrink-0 rounded-md">
                              <AvatarFallback
                                className={`rounded-md text-xs font-semibold text-white ${paletteFor(project.id)}`}
                              >
                                {project.name.slice(0, 2).toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                            <div className="min-w-0">
                              <div className="flex items-center gap-1.5 truncate text-sm font-medium text-foreground">
                                {project.name}
                                {project.archived_at && (
                                  <span className="shrink-0 rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-700">
                                    Archived
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {agentCountByProject.get(project.id) ?? 0} agent
                                {(agentCountByProject.get(project.id) ?? 0) ===
                                1
                                  ? ""
                                  : "s"}
                              </div>
                            </div>
                          </button>
                        ))}
                        {filteredProjects.length === 0 && (
                          <p className="p-2 text-sm text-muted-foreground">
                            No projects found.
                          </p>
                        )}
                      </>
                    )}
                  </div>
                  {isAdmin && (
                    <div className="border-t border-border p-2">
                      <Button
                        variant="ghost"
                        className="w-full justify-center gap-2 text-primary"
                        onClick={openCreate}
                      >
                        <Plus className="h-4 w-4" />
                        New project
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="flex h-full flex-col bg-background">
                <CardContent className="min-h-0 flex-1 space-y-4 p-4 sm:p-6">
                  {loading ? (
                    // Shape mirrors the selected-project header (avatar +
                    // name/subtitle), the two stat cards, and an assigned-
                    // agents row so the shimmer reads as "project detail
                    // incoming" rather than one blob.
                    <div className="space-y-4">
                      <div className="flex items-center gap-3">
                        <Skeleton className="h-10 w-10 shrink-0 rounded-md" />
                        <div className="space-y-2">
                          <Skeleton className="h-4 w-32" />
                          <Skeleton className="h-3 w-44" />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        {[0, 1].map((i) => (
                          <Card key={i} className="bg-background shadow-none">
                            <CardContent className="space-y-2 p-4">
                              <Skeleton className="h-3 w-28" />
                              <Skeleton className="h-7 w-16" />
                              <Skeleton className="h-3 w-36" />
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                      <div className="space-y-2">
                        <Skeleton className="h-4 w-32" />
                        <div className="flex min-h-[44px] items-center gap-2 rounded-md border border-border p-2">
                          <Skeleton className="h-6 w-6 shrink-0 rounded-full" />
                          <Skeleton className="h-4 w-24" />
                        </div>
                      </div>
                    </div>
                  ) : !selectedProject ? (
                    <p className="text-sm text-muted-foreground">
                      Select or create a project to see its details.
                    </p>
                  ) : (
                    <>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-10 w-10 shrink-0 rounded-md">
                            <AvatarFallback
                              className={`rounded-md text-sm font-semibold text-white ${paletteFor(selectedProject.id)}`}
                            >
                              {selectedProject.name.slice(0, 2).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="flex items-center gap-2 font-semibold text-foreground">
                              {selectedProject.name}
                              <span
                                className={
                                  selectedProject.archived_at
                                    ? "inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700"
                                    : "inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700"
                                }
                              >
                                <span
                                  className={
                                    selectedProject.archived_at
                                      ? "h-1.5 w-1.5 rounded-full bg-slate-500"
                                      : "h-1.5 w-1.5 rounded-full bg-emerald-500"
                                  }
                                />
                                {selectedProject.archived_at
                                  ? "Archived"
                                  : "Active"}
                              </span>
                            </div>
                            {selectedProject.description && (
                              <div className="text-sm text-muted-foreground">
                                {selectedProject.description}
                              </div>
                            )}
                          </div>
                        </div>
                        {isAdmin && (
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label="Edit project"
                              onClick={() => openEdit(selectedProject)}
                            >
                              <Settings className="h-4 w-4" />
                            </Button>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  aria-label="More actions"
                                >
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                  onClick={() =>
                                    handleToggleArchive(selectedProject)
                                  }
                                >
                                  <Archive className="mr-2 h-4 w-4" />
                                  {selectedProject.archived_at
                                    ? "Unarchive"
                                    : "Archive"}
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  className="text-destructive focus:bg-destructive/10 focus:text-destructive"
                                  onClick={() =>
                                    setDeleteTarget(selectedProject)
                                  }
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Delete project
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        )}
                      </div>

                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <Card className="bg-background shadow-none">
                          <CardContent className="space-y-2 p-4">
                            <div className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                              <Coins className="h-4 w-4" />
                              <span>Tokens (last 7 days)</span>
                            </div>
                            <div className="text-2xl font-semibold text-foreground">
                              {projectSpend.totalTokens.toLocaleString()}
                            </div>
                            <p className="text-xs text-muted-foreground">
                              {projectSpend.eventCount > 0 ? (
                                <>
                                  <UsageCostText totals={projectSpend} /> ·{" "}
                                  {LIST_PRICE_LABEL.toLowerCase()}
                                </>
                              ) : (
                                "No usage in the last 7 days"
                              )}
                            </p>
                            <TokenBreakdown
                              totals={projectSpend}
                              className="border-t border-border pt-2 sm:grid-cols-2"
                            />
                          </CardContent>
                        </Card>
                        <Card className="bg-background shadow-none">
                          <CardContent className="space-y-2 p-4">
                            <div className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                              <Bot className="h-4 w-4" />
                                <span>Connectors assigned</span>
                            </div>
                            <div className="text-2xl font-semibold text-foreground">
                              {projectAgents.length}
                            </div>
                            <p className="text-xs text-muted-foreground">
                              Set from each connector&apos;s own detail page
                            </p>
                          </CardContent>
                        </Card>
                      </div>

                      <div className="space-y-2">
                        <div className="text-sm font-semibold text-foreground">
                          Teams on this project
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Visibility/reporting only -- doesn&apos;t change which
                          connectors count toward the spend above.
                        </p>
                        {pods.length === 0 ? (
                          <p className="text-sm text-muted-foreground">
                            No teams set up yet.
                          </p>
                        ) : (
                          <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
                            {pods.map((pod) => (
                              <label
                                key={pod.id}
                                className={`flex min-h-[44px] items-center gap-2 rounded-md border border-border p-2 ${
                                  isAdmin
                                    ? "cursor-pointer hover:bg-muted/50"
                                    : ""
                                }`}
                              >
                                <Checkbox
                                  checked={assignedPodIds.has(pod.id)}
                                  disabled={
                                    !isAdmin || togglingPodId === pod.id
                                  }
                                  onCheckedChange={(checked) =>
                                    handleTogglePod(pod, checked === true)
                                  }
                                />
                                <span className="text-sm">{pod.name}</span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="space-y-2">
                        <div className="text-sm font-semibold text-foreground">
                          Members on this project
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Who&apos;s actually working on this project --
                          independent of team. Teams above are just a department
                          tag; adding someone here doesn&apos;t require their
                          team to be assigned too.
                        </p>
                        {teamMembers.filter((m) => m.auth_user_id).length ===
                        0 ? (
                          <p className="text-sm text-muted-foreground">
                            No members in this team yet.
                          </p>
                        ) : (
                          <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
                            {teamMembers
                              .filter(
                                (
                                  m,
                                ): m is MemberOption & {
                                  auth_user_id: string;
                                } => !!m.auth_user_id,
                              )
                              .map((member) => (
                                <label
                                  key={member.auth_user_id}
                                  className={`flex min-h-[44px] items-center gap-2 rounded-md border border-border p-2 ${
                                    isAdmin
                                      ? "cursor-pointer hover:bg-muted/50"
                                      : ""
                                  }`}
                                >
                                  <Checkbox
                                    checked={assignedMemberIds.has(
                                      member.auth_user_id,
                                    )}
                                    disabled={
                                      !isAdmin ||
                                      togglingMemberId === member.auth_user_id
                                    }
                                    onCheckedChange={(checked) =>
                                      handleToggleMember(
                                        member,
                                        checked === true,
                                      )
                                    }
                                  />
                                  <span className="truncate text-sm">
                                    {memberDisplayName(member)}
                                  </span>
                                </label>
                              ))}
                          </div>
                        )}
                      </div>

                      <div className="space-y-2">
                        <div className="text-sm font-semibold text-foreground">
                          Assigned connectors
                        </div>
                        {projectAgents.length === 0 ? (
                          <p className="text-sm text-muted-foreground">
                            No connectors pinned to this project yet.
                          </p>
                        ) : (
                          <div className="space-y-1">
                            {projectAgents.map((a) => (
                              <div
                                key={a.id}
                                className="flex min-h-[44px] items-center gap-2 rounded-md border border-border p-2 text-sm"
                              >
                                <Avatar className="h-6 w-6">
                                  <AvatarFallback className="text-[10px] font-semibold">
                                    {initialsOf(a.name)}
                                  </AvatarFallback>
                                </Avatar>
                                <span className="font-medium text-foreground">
                                  {a.name}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {!error && (isAdmin || isManager) && (
            <ProjectAttribution
              teamId={teamId}
              isAdmin={isAdmin}
              projects={projects}
              onRulesChanged={refreshUsage}
            />
          )}
        </main>
      </SidebarInset>

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader>
            <SheetTitle>
              {sheetMode === "create" ? "New project" : "Edit project"}
            </SheetTitle>
          </SheetHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">
                Project name
              </label>
              <Input
                placeholder="e.g, Marketing"
                value={sheetName}
                onChange={(e) => setSheetName(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">
                Description
              </label>
              <textarea
                className="min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                placeholder="Optional"
                value={sheetDescription}
                onChange={(e) => setSheetDescription(e.target.value)}
              />
            </div>
          </div>
          <SheetFooter>
            <Button variant="outline" onClick={() => setSheetOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={!sheetName.trim() || saving}
              onClick={handleSaveProject}
            >
              <Plus className="mr-1.5 h-4 w-4" />
              {saving
                ? "Saving…"
                : sheetMode === "create"
                  ? "Create Project"
                  : "Save Changes"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{deleteTarget?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              Connectors pinned to it will become unassigned. This can&apos;t be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className={buttonVariants({ variant: "destructive" })}
              disabled={deleting}
              onClick={(e) => {
                e.preventDefault();
                handleConfirmDelete();
              }}
            >
              {deleting ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SidebarProvider>
  );
};

export default Projects;
